<template>
  <div v-if="isOpen" class="fixed inset-0 z-50 overflow-y-auto">
    <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <div class="fixed inset-0 transition-opacity" @click="close">
        <div class="absolute inset-0 bg-black opacity-50"></div>
      </div>

      <div class="inline-block align-bottom bg-background-light rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
        <div class="bg-background-light px-6 pt-5 pb-3 border-b border-background-lighter">
          <div class="flex items-start justify-between">
            <div>
              <h3 class="text-lg font-semibold text-text-primary">Messages</h3>
              <p class="text-sm text-text-secondary mt-1">
                #ESK-{{ order.id }} — {{ order.article_libelle || order.article_title || '' }}
              </p>
            </div>
            <button @click="close" class="text-text-secondary hover:text-text-primary transition-colors ml-4">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        </div>

        <div class="bg-background-light px-6 py-4">
          <div ref="chatBox" class="chat-box" style="max-height: 400px; min-height: 300px; overflow-y: auto; padding: 12px; background: #f0f2f5; border-radius: 10px;">
            <div v-if="isLoadingMessages" class="flex justify-center items-center py-8">
              <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              <span class="ml-2 text-sm text-text-secondary">Loading messages...</span>
            </div>

            <div v-else-if="messages.length === 0" class="text-center py-8">
              <svg class="w-12 h-12 text-text-secondary mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"></path>
              </svg>
              <p class="text-sm text-text-secondary">No messages yet. Start the conversation.</p>
            </div>

            <div v-else>
              <div v-for="msg in messages" :key="msg.id" class="mb-3" :class="msg.admin_id ? 'text-left' : 'text-right'">
                <div :class="msg.admin_id ? 'admin-bubble' : 'user-bubble'" class="inline-block px-4 py-3 rounded-2xl max-w-xs lg:max-w-md text-sm shadow-sm" style="word-wrap: break-word; white-space: pre-wrap;">
                  <p class="leading-relaxed" style="margin: 0;">{{ msg.message }}</p>
                </div>
                <div class="flex items-center mt-1" :class="msg.admin_id ? 'justify-start' : 'justify-end'" style="gap: 4px;">
                  <small class="text-xs text-gray-400">{{ formatTime(msg.created_at) }}</small>
                  <small v-if="!msg.admin_id && msg.user_read_at" class="text-xs text-green-500">&#10003;</small>
                </div>
              </div>
            </div>
          </div>

          <div v-if="isChatDisabled" class="mt-3 p-4 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800/50 dark:to-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-xl text-center">
            <div class="flex flex-col items-center gap-2">
              <div class="p-2.5 bg-gray-200 dark:bg-gray-700 rounded-full">
                <svg class="w-6 h-6 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"/>
                </svg>
              </div>
              <div>
                <p class="text-sm font-semibold text-gray-700 dark:text-gray-300">Chat Disabled</p>
                <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5 max-w-xs mx-auto leading-relaxed">
                  The seller has disabled chat for this order. You can still view all previous messages, but sending new messages is not available at this time. If you need assistance, please contact our support team.
                </p>
              </div>
            </div>
          </div>

          <form @submit.prevent="sendMessage" class="mt-4">
            <div class="flex items-end space-x-2">
              <div class="flex-1">
                <textarea
                    v-model="newMessage"
                    @keydown.enter.exact.prevent="sendMessage"
                    :disabled="isSending || isChatDisabled"
                    class="w-full px-3 py-2 border border-background-lighter rounded-lg bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-text-primary placeholder-text-secondary resize-none"
                    rows="2"
                    placeholder="Type your message..."
                    maxlength="1000"
                ></textarea>
              </div>
              <button
                  type="submit"
                  :disabled="!newMessage.trim() || isSending || isChatDisabled"
                  class="px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
              >
                <svg v-if="!isSending" class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z"></path>
                </svg>
                <div v-else class="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              </button>
            </div>
            <div class="flex justify-between items-center mt-1">
              <small class="text-xs text-text-secondary">{{ newMessage.length }}/1000</small>
              <small class="text-xs text-text-secondary">Enter to send</small>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, watch, nextTick, onUnmounted } from 'vue';
import { getOrderMessages, sendOrderMessage, markAllMessagesRead } from '../services/api';
import { getEcho } from '../services/echo';

export default {
  name: 'ChatModal',
  props: {
    isOpen: { type: Boolean, required: true },
    order: { type: Object, required: true }
  },
  setup(props, { emit }) {
    const messages = ref([]);
    const newMessage = ref('');
    const isSending = ref(false);
    const isLoadingMessages = ref(false);
    const isChatDisabled = ref(false);
    const chatBox = ref(null);

    let pollInterval = null;
    let echoChannel = null;

    const fetchMessages = async () => {
      try {
        const res = await getOrderMessages(props.order.id);
        if (res && res.success) {
          const newData = res.data?.data || [];
          messages.value = newData;

          const hasUnread = newData.some(m => m.admin_id && !m.user_read_at);
          if (hasUnread) {
            markAllMessagesRead(props.order.id).catch(() => {});
          }

          await nextTick();
          scrollToBottom();
        }
      } catch (err) {
        console.error('Failed to fetch messages:', err);
      }
    };

    const scrollToBottom = () => {
      if (chatBox.value) {
        chatBox.value.scrollTop = chatBox.value.scrollHeight;
      }
    };

    const sendMessage = async () => {
      const text = newMessage.value.trim();
      if (!text || isSending.value || isChatDisabled.value) return;

      isSending.value = true;

      try {
        const res = await sendOrderMessage(props.order.id, text);
        if (res && res.success) {
          newMessage.value = '';
          await fetchMessages();
          await nextTick();
          scrollToBottom();
        }
      } catch (err) {
        const data = err.response?.data;
        if (data?.errors?.message) {
          const msg = Array.isArray(data.errors.message) ? data.errors.message[0] : data.errors.message;
          if (msg && msg.toLowerCase().includes('disabled')) {
            isChatDisabled.value = true;
          }
        } else if (data?.message && data.message.toLowerCase().includes('disabled')) {
          isChatDisabled.value = true;
        }
      } finally {
        isSending.value = false;
      }
    };

    const formatTime = (dateStr) => {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    };

    const subscribeToChannel = () => {
      const echo = getEcho();
      if (!echo) return;
      if (echoChannel) echoChannel.stopListening('.message.sent');
      echoChannel = echo.private(`commande.${props.order.id}`);
      echoChannel.listen('.message.sent', (data) => {
        const exists = messages.value.some(m => m.id === data.id);
        if (!exists) {
          messages.value.push(data);
          nextTick(() => scrollToBottom());
        }
      });
    };

    const unsubscribeFromChannel = () => {
      if (echoChannel) {
        echoChannel.stopListening('.message.sent');
        echoChannel = null;
      }
    };

    watch(() => props.isOpen, async (open) => {
      if (open) {
        isLoadingMessages.value = true;
        isChatDisabled.value = !!props.order.chat_disabled;
        messages.value = [];
        await fetchMessages();
        isLoadingMessages.value = false;
        if (pollInterval) clearInterval(pollInterval);
        pollInterval = setInterval(fetchMessages, 30000);
        subscribeToChannel();
      } else {
        unsubscribeFromChannel();
        if (pollInterval) clearInterval(pollInterval);
        pollInterval = null;
      }
    }, { immediate: true });

    const close = () => {
      unsubscribeFromChannel();
      if (pollInterval) clearInterval(pollInterval);
      pollInterval = null;
      emit('close');
    };

    onUnmounted(() => {
      unsubscribeFromChannel();
      if (pollInterval) clearInterval(pollInterval);
    });

    return {
      messages, newMessage, isSending, isLoadingMessages,
      isChatDisabled, chatBox,
      sendMessage, formatTime, close,
    };
  }
};
</script>

<style scoped>
.admin-bubble {
  background: #e4e6eb;
  color: #1a2332;
  border-bottom-left-radius: 4px;
}
.user-bubble {
  background: #6a17d2;
  color: #fff;
  border-bottom-right-radius: 4px;
}
.dark .admin-bubble {
  background: #3a3b3c;
  color: #e4e6eb;
}
.dark .user-bubble {
  background: #5a0fb3;
  color: #fff;
}
.chat-box::-webkit-scrollbar {
  width: 6px;
}
.chat-box::-webkit-scrollbar-track {
  background: transparent;
}
.chat-box::-webkit-scrollbar-thumb {
  background: #c4c4c4;
  border-radius: 3px;
}
</style>
